import React, { useEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Spinner } from '@kaseyaitglue/kaseya-ui';
import { tokenService } from '@root/v2/api';
import useRedirect from '@root/v2/hooks';
import { AuthPanelWrapperStyled, SpinnerContainerStyled } from '@features/auth/components/AuthPanelWrapper';
import { AUTH_ROUTES } from '@features/auth/constants';
import { clearResumeRedirect, hasRecentResumeRedirect, markResumeRedirect } from '@features/auth/resumeRedirect';
import { hasTFAToken, refreshSession } from '@features/auth/services';
import getPostAuthDestination from '@features/auth/utils/postAuthDestination';
import { GuestRouteProps } from './types';

type GuestRouteStatus = 'checking' | 'unauthenticated' | 'no-pending-tfa';

const TEST_ID_PREFIX = 'guest-route';

const hasStoredSession = (): boolean => {
  try {
    return tokenService.hasAnyToken();
  } catch {
    return false;
  }
};

const hasLiveSession = async (): Promise<boolean> => {
  try {
    return await refreshSession();
  } catch {
    return false;
  }
};

const getGuestStatus = (shouldRequirePendingTFA?: boolean): GuestRouteStatus => (
  shouldRequirePendingTFA && !hasTFAToken() ? 'no-pending-tfa' : 'unauthenticated'
);

const GuestRoute = ({ shouldRequirePendingTFA }: GuestRouteProps) => {
  const redirect = useRedirect();
  const redirectRef = useRef(redirect);
  const { search } = useLocation();
  const [status, setStatus] = useState<GuestRouteStatus>(
    () => (hasStoredSession() && !hasRecentResumeRedirect() ? 'checking' : getGuestStatus(shouldRequirePendingTFA)),
  );

  useEffect(() => {
    redirectRef.current = redirect;
  });

  useEffect(() => {
    if (status !== 'checking') {
      clearResumeRedirect();

      return undefined;
    }

    let isMounted = true;

    const check = async () => {
      const isSessionLive = await hasLiveSession();

      if (!isMounted) return;

      if (isSessionLive) {
        markResumeRedirect();
        redirectRef.current(getPostAuthDestination());

        return;
      }

      setStatus(getGuestStatus(shouldRequirePendingTFA));
    };

    check();

    return () => { isMounted = false; };
  }, [status, shouldRequirePendingTFA]);

  if (status === 'no-pending-tfa') {
    return <Navigate to={`${AUTH_ROUTES.LOGIN}${search}`} replace />;
  }

  if (status === 'unauthenticated') {
    return <Outlet />;
  }

  return (
    <AuthPanelWrapperStyled data-testid={`${TEST_ID_PREFIX}-page`}>
      <SpinnerContainerStyled data-testid={`${TEST_ID_PREFIX}-loading-block`}>
        <Spinner size="large" />
      </SpinnerContainerStyled>
    </AuthPanelWrapperStyled>
  );
};

export default GuestRoute;
